import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../_components/page-hero";

const TIMELINE = [
  { year: "01", title: "Booking", body: "Set dates, check availability, enter rent and deposit, then select the customer. The agreed pricing is frozen at creation." },
  { year: "02", title: "Pickup", body: "Verify KYC with consent, complete the photo-backed checklist and collect any required deposit before starting the trip." },
  { year: "03", title: "Trip and return", body: "Monitor expected return and overdue status. Record odometer, fuel, photos, damage and accessories when the vehicle returns." },
  { year: "04", title: "Settlement", body: "Generate the bill, record payments and settle the deposit. A fully paid, settled booking completes and locks." },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About RentOS"
        title={<>Built around the <span className="gradient-text">rental counter.</span></>}
        sub="One Business app for bookings, customer KYC, fleet records, payments, deposits, agreements and reports."
      />

      {/* Story */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <article className="max-w-none text-base leading-relaxed text-foreground/90">
            <p className="font-display text-xl">
              RentOS is rental management software for self-drive bike, scooter and car businesses in India.
            </p>
            <p className="mt-5 text-muted-foreground">
              A paper register, WhatsApp messages and a folder of UPI screenshots leave the owner piecing together what happened. Without a recorded pickup, return condition and deposit history, a disputed rental is difficult to reconcile.
            </p>
            <p className="mt-5 text-muted-foreground">
              RentOS records that loop in one app: booking, counter KYC with consent, deposit collection, pickup checklist, active trip, return, settlement and invoice. Each stage keeps its records against the booking.
            </p>
            <p className="mt-5 text-muted-foreground">
              The product is live on the web and as native Android and iOS apps. It is designed for one owner per business, typically managing around 5–50 vehicles.
            </p>
          </article>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="border-y border-border bg-surface/30 py-20">
        <div className="mx-auto grid max-w-5xl gap-12 px-4 sm:px-6 md:grid-cols-2">
          <div>
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">Mission</div>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              Give the rental owner a clear record of every trip.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Keep agreed pricing, verified customer records, handover photos, payments and deposit settlement together instead of across notebooks and galleries.
            </p>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-accent">Vision</div>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              The same Business app, wherever the owner works.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Use a desktop or phone browser, or the native Android and iOS app with camera capture, file selection, sharing and push notifications.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-center">
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">The rental loop</div>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">From booking to settlement.</h2>
          </div>
          <ol className="mt-14 space-y-10 border-l border-border pl-8">
            {TIMELINE.map((t) => (
              <li key={t.year} className="relative">
                <span className="absolute -left-[34px] grid h-5 w-5 place-items-center rounded-full border border-primary bg-background">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                </span>
                <div className="font-mono text-xs uppercase tracking-wider text-primary">{t.year}</div>
                <h3 className="mt-1 font-display text-lg font-semibold">{t.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Workflow note */}
      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <p className="font-display text-2xl leading-snug sm:text-3xl">
            &ldquo;One booking. A verified customer. A photo-backed handover. A recorded return.
            A settled deposit.&rdquo;
          </p>
          <p className="mt-6 text-sm text-muted-foreground">The RentOS Business workflow</p>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 rounded-xl bg-gradient-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-glow"
          >
            Book a demo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
